import { useEffect, useMemo, useRef, useState } from "react";
import {
  deleteProfile,
  getProfile,
  normalizeVoiceProfile,
  saveActiveVoice,
  saveProfile,
} from "../utils/profile";
import {
  applyEditorToVoices,
  arrayToLines,
  buildEditorProfile,
  editorFieldsFromVoice,
  linesToArray,
  persistBrandSettings,
} from "../utils/voiceSelection";
import { supabase } from "../lib/supabaseClient";
import { safeCapture } from "../utils/analytics";

const makeId = () => crypto.randomUUID();

function buildHelperDraft(current) {
  const desired = linesToArray(current.helper_desired);
  const avoid = linesToArray(current.helper_avoid);
  const say = linesToArray(current.helper_say);
  const neverSay = linesToArray(current.helper_never_say);

  const summaryParts = [
    desired.length ? desired.join(", ") : "clear, grounded, human",
    current.playfulness_level < 0.3
      ? "low-playfulness"
      : current.playfulness_level < 0.6
      ? "lightly playful"
      : "noticeably playful",
    current.snark_level < 0.3
      ? "not snarky"
      : current.snark_level < 0.6
      ? "light edge"
      : "sharp but controlled",
    current.formality_level < 0.35
      ? "casual"
      : current.formality_level < 0.7
      ? "balanced professional"
      : "polished professional",
  ];

  const styleRules = [
    "Prefer direct, plainspoken wording.",
    "Specific beats generic.",
    "Clarity comes before cleverness.",
    ...(desired.length ? [`Lean into: ${desired.join(", ")}.`] : []),
  ];

  const negativePrompt = [
    "Do not sound like a generic AI assistant.",
    "Do not use corporate buzzwords or fake enthusiasm.",
    "Do not force humor, snark, or personality into every line.",
    ...(avoid.length ? [`Do not sound ${avoid.join(", ")}.`] : []),
  ];

  const bannedPhrases = [
    "unlock",
    "game-changer",
    "elevate your",
    "delve into",
    ...neverSay,
  ];

  const summary = current.summary?.trim()
    ? current.summary.trim()
    : `A ${summaryParts.join(", ")} voice that sounds human, natural, and trustworthy.`;

  return normalizeVoiceProfile({
    ...current,
    summary,
    style_rules: [...new Set(styleRules)],
    negative_prompt: [...new Set(negativePrompt)],
    banned_phrases: [...new Set(bannedPhrases)],
    sample_phrases: say,
    avoid_phrases: neverSay,
    source_confidence: say.length || neverSay.length ? "helper_plus_examples" : "helper_sparse",
    helper_notes: {
      desired,
      avoid,
      say,
      neverSay,
    },
  });
}

function getPlayfulnessLabel(level) {
  if (level < 0.2) return "Reserved";
  if (level < 0.45) return "Light";
  if (level < 0.7) return "Balanced";
  return "Playful";
}

function getSnarkLabel(level) {
  if (level < 0.2) return "Polite";
  if (level < 0.45) return "Light Edge";
  if (level < 0.7) return "Direct";
  return "Sharp";
}

function getWarmthLabel(level) {
  if (level < 0.2) return "Cool";
  if (level < 0.45) return "Measured";
  if (level < 0.7) return "Warm";
  return "Very Warm";
}

function getFormalityLabel(level) {
  if (level < 0.2) return "Casual";
  if (level < 0.45) return "Relaxed Pro";
  if (level < 0.7) return "Balanced";
  return "Polished";
}

function makeBlankVoice(index = 1) {
  return {
    id: makeId(),
    name: `Voice ${index}`,
    text: "",
    profile: normalizeVoiceProfile({
      voice_name: `Voice ${index}`,
      summary: "",
      playfulness_level: 0.35,
      snark_level: 0.3,
      warmth_level: 0.65,
      formality_level: 0.55,
      emoji_tolerance: "low",
      cta_style: "direct",
      style_rules: [],
      negative_prompt: [],
      banned_phrases: [],
      sample_phrases: [],
      avoid_phrases: [],
      helper_notes: {},
      source_confidence: "manual",
    }),
    updated_at: new Date().toISOString(),
  };
}

export default function BrandSettings() {
  const [userEmail, setUserEmail] = useState("");
  const [voices, setVoices] = useState([]);
  const [activeVoiceId, setActiveVoiceId] = useState(null);

  const [voiceName, setVoiceName] = useState("");
  const [summary, setSummary] = useState("");
  const [playfulnessLevel, setPlayfulnessLevel] = useState(0.35);
  const [snarkLevel, setSnarkLevel] = useState(0.3);
  const [warmthLevel, setWarmthLevel] = useState(0.65);
  const [formalityLevel, setFormalityLevel] = useState(0.55);
  const [emojiTolerance, setEmojiTolerance] = useState("low");
  const [ctaStyle, setCtaStyle] = useState("direct");
  const [styleRulesText, setStyleRulesText] = useState("");
  const [negativePromptText, setNegativePromptText] = useState("");
  const [bannedPhrasesText, setBannedPhrasesText] = useState("");
  const [samplePhrasesText, setSamplePhrasesText] = useState("");
  const [avoidPhrasesText, setAvoidPhrasesText] = useState("");

  const [helperDesired, setHelperDesired] = useState("");
  const [helperAvoid, setHelperAvoid] = useState("");
  const [helperSay, setHelperSay] = useState("");
  const [helperNeverSay, setHelperNeverSay] = useState("");

  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);
  const [hasExistingProfile, setHasExistingProfile] = useState(false);

  // The voices as they are saved in the database. Save compares against these to
  // tell an active-voice switch from a real voice change.
  const savedVoicesRef = useRef([]);

  const activeVoice = useMemo(
    () => voices.find((v) => v.id === activeVoiceId) || null,
    [voices, activeVoiceId]
  );

  function loadVoiceIntoEditor(voice) {
    const fields = editorFieldsFromVoice(voice);

    setVoiceName(fields.voiceName);
    setSummary(fields.summary);
    setPlayfulnessLevel(fields.playfulnessLevel);
    setSnarkLevel(fields.snarkLevel);
    setWarmthLevel(fields.warmthLevel);
    setFormalityLevel(fields.formalityLevel);
    setEmojiTolerance(fields.emojiTolerance);
    setCtaStyle(fields.ctaStyle);
    setStyleRulesText(fields.styleRulesText);
    setNegativePromptText(fields.negativePromptText);
    setBannedPhrasesText(fields.bannedPhrasesText);
    setSamplePhrasesText(fields.samplePhrasesText);
    setAvoidPhrasesText(fields.avoidPhrasesText);

    setHelperDesired(fields.helperDesired);
    setHelperAvoid(fields.helperAvoid);
    setHelperSay(fields.helperSay);
    setHelperNeverSay(fields.helperNeverSay);
  }

  function currentEditorFields() {
    return {
      voiceName,
      summary,
      playfulnessLevel,
      snarkLevel,
      warmthLevel,
      formalityLevel,
      emojiTolerance,
      ctaStyle,
      styleRulesText,
      negativePromptText,
      bannedPhrasesText,
      samplePhrasesText,
      avoidPhrasesText,
      helperDesired,
      helperAvoid,
      helperSay,
      helperNeverSay,
    };
  }

  function collectEditorProfile() {
    return buildEditorProfile(currentEditorFields());
  }

  function syncEditorIntoVoices() {
    return applyEditorToVoices(voices, activeVoiceId, currentEditorFields());
  }

  useEffect(() => {
    let isMounted = true;

    (async () => {
      setMessage(null);

      const { data: auth } = await supabase.auth.getUser();
      const email = auth?.user?.email || "";
      if (isMounted) setUserEmail(email);

      if (!auth?.user?.id) {
        if (!isMounted) return;
        setHasExistingProfile(false);
        setVoices([]);
        setActiveVoiceId(null);
        setMessage({ type: "error", text: "Please sign in to manage brand settings." });
        return;
      }

      try {
        const profile = await getProfile();
        if (!isMounted) return;

        if (!profile) {
          savedVoicesRef.current = [];
          setHasExistingProfile(false);
          setVoices([]);
          setActiveVoiceId(null);
          const blank = makeBlankVoice(1);
          setVoices([blank]);
          setActiveVoiceId(blank.id);
          loadVoiceIntoEditor(blank);
          return;
        }

        setHasExistingProfile(true);

        const loadedVoices = Array.isArray(profile.voices) ? profile.voices : [];
        savedVoicesRef.current = loadedVoices;
        const safeVoices = loadedVoices.length ? loadedVoices : [makeBlankVoice(1)];
        setVoices(safeVoices);

        const nextActiveId = profile.active_voice_id || safeVoices[0]?.id || null;
        setActiveVoiceId(nextActiveId);

        const nextActive =
          safeVoices.find((v) => v.id === nextActiveId) || safeVoices[0] || null;

        loadVoiceIntoEditor(nextActive);
        setMessage({ type: "info", text: "Loaded your brand settings." });
      } catch (err) {
        if (!isMounted) return;
        setMessage({ type: "error", text: err?.message || "Failed to load settings." });
      }
    })();

    return () => {
      isMounted = false;
    };
  }, []);

  function selectVoice(id) {
    const nextVoices = syncEditorIntoVoices();
    setVoices(nextVoices);

    setActiveVoiceId(id);
    const voice = nextVoices.find((x) => x.id === id);
    loadVoiceIntoEditor(voice);
    setMessage(null);
    safeCapture("brand_voice_switched");
  }

  function createNewVoice() {
    setMessage(null);

    const synced = syncEditorIntoVoices();
    if (synced.length >= 5) {
      setMessage({ type: "error", text: "Max 5 brand voices allowed." });
      return;
    }

    const newVoice = makeBlankVoice(synced.length + 1);
    const next = [...synced, newVoice];

    setVoices(next);
    setActiveVoiceId(newVoice.id);
    loadVoiceIntoEditor(newVoice);
    safeCapture("brand_voice_added", { voice_count: next.length });
  }

  function deleteSelectedVoiceLocal() {
    setMessage(null);
    if (!activeVoiceId) return;

    const next = voices.filter((v) => v.id !== activeVoiceId);
    const safeNext = next.length ? next : [makeBlankVoice(1)];

    setVoices(safeNext);

    const nextActiveId = safeNext[0]?.id || null;
    setActiveVoiceId(nextActiveId);
    loadVoiceIntoEditor(safeNext[0] || null);

    setMessage({
      type: "success",
      text: "Voice removed. Click Save Settings to keep this change.",
    });
    safeCapture("brand_voice_removed", { remaining_count: safeNext.length });
  }

  function generateFromHelper() {
    const draft = buildHelperDraft({
      summary,
      playfulness_level: playfulnessLevel,
      snark_level: snarkLevel,
      warmth_level: warmthLevel,
      formality_level: formalityLevel,
      emoji_tolerance: emojiTolerance,
      cta_style: ctaStyle,
      helper_desired: helperDesired,
      helper_avoid: helperAvoid,
      helper_say: helperSay,
      helper_never_say: helperNeverSay,
    });

    setSummary(draft.summary || "");
    setStyleRulesText(arrayToLines(draft.style_rules));
    setNegativePromptText(arrayToLines(draft.negative_prompt));
    setBannedPhrasesText(arrayToLines(draft.banned_phrases));
    setSamplePhrasesText(arrayToLines(draft.sample_phrases));
    setAvoidPhrasesText(arrayToLines(draft.avoid_phrases));
    setMessage({
      type: "success",
      text: "Built a starter voice from your notes. Tweak anything below, then click Save Settings.",
    });
    safeCapture("brand_voice_quick_setup_used");
  }

  async function onSave(e) {
    e.preventDefault();
    setMessage(null);
    setLoading(true);

    try {
      let nextVoices = syncEditorIntoVoices();
      let nextActiveId = activeVoiceId;

      if (!nextActiveId) {
        const newVoice = makeBlankVoice(nextVoices.length + 1);
        const newProfile = collectEditorProfile();

        const hydrated = {
          ...newVoice,
          name: voiceName || newVoice.name,
          text: newProfile.summary || "",
          profile: newProfile,
          updated_at: new Date().toISOString(),
        };

        nextVoices = [...nextVoices, hydrated];
        nextActiveId = hydrated.id;
        setActiveVoiceId(hydrated.id);
      }

      // A switch of active voice saves only that setting; any voice change still
      // saves every voice. Throws on failure, so success is shown only after this.
      const plan = await persistBrandSettings(
        {
          loadedVoices: savedVoicesRef.current,
          voices: nextVoices,
          activeVoiceId: nextActiveId,
          snarkLevel,
        },
        { saveActiveVoice, saveProfile }
      );

      if (plan.kind === "full") savedVoicesRef.current = nextVoices;

      setVoices(nextVoices);
      setHasExistingProfile(true);
      setMessage({
        type: "success",
        text: plan.kind === "active_only" ? "Active voice saved." : "Brand settings saved.",
      });
      safeCapture("brand_voice_saved", { voice_count: nextVoices.length, save_kind: plan.kind });
    } catch (err) {
      setMessage({ type: "error", text: err?.message || "Failed to save settings." });
      safeCapture("brand_voice_save_failed");
    } finally {
      setLoading(false);
    }
  }

  async function onDeleteProfile() {
    setMessage(null);
    if (!window.confirm("Delete your saved brand settings?")) return;

    setLoading(true);
    try {
      await deleteProfile();
      savedVoicesRef.current = [];
      const blank = makeBlankVoice(1);
      setHasExistingProfile(false);
      setVoices([blank]);
      setActiveVoiceId(blank.id);
      loadVoiceIntoEditor(blank);
      setMessage({ type: "success", text: "Brand settings deleted." });
      safeCapture("brand_voice_profile_deleted");
    } catch (err) {
      setMessage({ type: "error", text: err?.message || "Failed to delete settings." });
      safeCapture("brand_voice_profile_delete_failed");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="max-w-4xl mx-auto bg-white p-6 rounded-lg shadow">
      <h1 className="text-2xl font-bold text-gray-900 mb-1">Brand Settings</h1>
      <p className="text-sm text-gray-600 mb-2">
        Set up how your business should sound in everything DobieCore creates.
        Save up to 5 voices and pick one as your active voice — Captions, Blog,
        and Prewriting will use it automatically.
      </p>

      <p className="text-xs text-gray-500 mb-6">
        Signed in as: <span className="font-medium">{userEmail || "—"}</span>
        {hasExistingProfile ? " • Settings found" : " • No saved settings yet"}
      </p>

      <form onSubmit={onSave} className="space-y-6">
        <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-semibold text-gray-900">
              Saved Brand Voices <span className="text-gray-500">({voices.length}/5)</span>
            </h3>

            <div className="flex gap-2">
              <button
                type="button"
                onClick={createNewVoice}
                className="px-3 py-1.5 rounded bg-gray-200 text-gray-800 text-sm"
              >
                + New
              </button>
              <button
                type="button"
                onClick={deleteSelectedVoiceLocal}
                disabled={!activeVoiceId}
                className="px-3 py-1.5 rounded bg-red-100 text-red-700 text-sm disabled:opacity-50"
              >
                Remove voice
              </button>
            </div>
          </div>

          <div className="grid gap-3">
            <select
              className="w-full border rounded px-3 py-2"
              value={activeVoiceId || ""}
              onChange={(e) => selectVoice(e.target.value)}
              disabled={voices.length === 0}
            >
              {voices.length === 0 ? (
                <option value="">No voices yet — click “New”</option>
              ) : (
                voices.map((v) => (
                  <option key={v.id} value={v.id}>
                    {v.name || "Untitled voice"}
                  </option>
                ))
              )}
            </select>

            <input
              className="w-full border rounded px-3 py-2"
              placeholder="Voice name (e.g., Bluedobie Classic)"
              value={voiceName}
              onChange={(e) => setVoiceName(e.target.value)}
            />

            {activeVoice && (
              <p className="text-xs text-gray-600">
                Active voice: <span className="font-medium">{activeVoice.name || "Untitled"}</span>
              </p>
            )}
          </div>
        </div>

        <div className="border border-blue-200 rounded-lg p-4 bg-blue-50">
          <div className="flex items-center justify-between gap-3 mb-3">
            <div>
              <h3 className="text-sm font-semibold text-gray-900">Quick Setup</h3>
              <p className="text-xs text-gray-600 mt-1">
                Not sure where to start? Jot a few notes here and we'll build a
                starter voice for you. You can fine-tune it below.
              </p>
            </div>

            <button
              type="button"
              onClick={generateFromHelper}
              className="px-3 py-2 rounded bg-sabre-blue text-white text-sm font-medium hover:bg-sabre-blue/90"
            >
              Build Starter Voice
            </button>
          </div>

          <div className="grid md:grid-cols-2 gap-3">
            <textarea
              className="w-full border rounded px-3 py-2"
              rows={4}
              placeholder="How should this voice feel? One item per line. Example:
clear
grounded
trustworthy
a little witty"
              value={helperDesired}
              onChange={(e) => setHelperDesired(e.target.value)}
            />

            <textarea
              className="w-full border rounded px-3 py-2"
              rows={4}
              placeholder="How should it never sound? One item per line. Example:
salesy
fake-cheerful
corporate
try-hard"
              value={helperAvoid}
              onChange={(e) => setHelperAvoid(e.target.value)}
            />

            <textarea
              className="w-full border rounded px-3 py-2"
              rows={4}
              placeholder="Phrases you would actually say. One per line."
              value={helperSay}
              onChange={(e) => setHelperSay(e.target.value)}
            />

            <textarea
              className="w-full border rounded px-3 py-2"
              rows={4}
              placeholder="Phrases you'd never say. One per line."
              value={helperNeverSay}
              onChange={(e) => setHelperNeverSay(e.target.value)}
            />
          </div>
        </div>

        <div className="border border-gray-200 rounded-lg p-4">
          <h3 className="text-sm font-semibold text-gray-900 mb-3">Voice Details</h3>

          <div className="grid gap-4">
            <textarea
              className="w-full border rounded px-3 py-2"
              rows={4}
              placeholder="One-sentence summary of this voice. Example: Clear, grounded, trustworthy, mildly witty, never fake-peppy."
              value={summary}
              onChange={(e) => setSummary(e.target.value)}
            />

            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Playfulness: <span className="font-semibold">{getPlayfulnessLabel(playfulnessLevel)}</span>
                </label>
                <input
                  type="range"
                  min="0"
                  max="1"
                  step="0.05"
                  value={playfulnessLevel}
                  onChange={(e) => setPlayfulnessLevel(parseFloat(e.target.value))}
                  className="w-full"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Snark / Edge: <span className="font-semibold">{getSnarkLabel(snarkLevel)}</span>
                </label>
                <input
                  type="range"
                  min="0"
                  max="1"
                  step="0.05"
                  value={snarkLevel}
                  onChange={(e) => setSnarkLevel(parseFloat(e.target.value))}
                  className="w-full"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Warmth: <span className="font-semibold">{getWarmthLabel(warmthLevel)}</span>
                </label>
                <input
                  type="range"
                  min="0"
                  max="1"
                  step="0.05"
                  value={warmthLevel}
                  onChange={(e) => setWarmthLevel(parseFloat(e.target.value))}
                  className="w-full"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Formality: <span className="font-semibold">{getFormalityLabel(formalityLevel)}</span>
                </label>
                <input
                  type="range"
                  min="0"
                  max="1"
                  step="0.05"
                  value={formalityLevel}
                  onChange={(e) => setFormalityLevel(parseFloat(e.target.value))}
                  className="w-full"
                />
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Emoji Tolerance
                </label>
                <select
                  className="w-full border rounded px-3 py-2"
                  value={emojiTolerance}
                  onChange={(e) => setEmojiTolerance(e.target.value)}
                >
                  <option value="none">None</option>
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Call to action style
                </label>
                <select
                  className="w-full border rounded px-3 py-2"
                  value={ctaStyle}
                  onChange={(e) => setCtaStyle(e.target.value)}
                >
                  <option value="soft">Soft</option>
                  <option value="direct">Direct</option>
                  <option value="punchy">Punchy</option>
                </select>
                <p className="mt-1 text-xs text-gray-500">
                  How you ask customers to take the next step.
                </p>
              </div>
            </div>

            <textarea
              className="w-full border rounded px-3 py-2"
              rows={5}
              placeholder="How this voice should sound — one rule per line. Example: Prefer direct, plainspoken wording."
              value={styleRulesText}
              onChange={(e) => setStyleRulesText(e.target.value)}
            />

            <textarea
              className="w-full border rounded px-3 py-2"
              rows={5}
              placeholder="Things this voice should never do — one per line. Example: Sound like a generic AI assistant."
              value={negativePromptText}
              onChange={(e) => setNegativePromptText(e.target.value)}
            />

            <div className="grid md:grid-cols-2 gap-4">
              <textarea
                className="w-full border rounded px-3 py-2"
                rows={5}
                placeholder="Words this voice should never use — one per line."
                value={bannedPhrasesText}
                onChange={(e) => setBannedPhrasesText(e.target.value)}
              />

              <textarea
                className="w-full border rounded px-3 py-2"
                rows={5}
                placeholder="Phrases that sound like you — one per line. We'll echo the vibe without copying word-for-word."
                value={samplePhrasesText}
                onChange={(e) => setSamplePhrasesText(e.target.value)}
              />
            </div>

            <textarea
              className="w-full border rounded px-3 py-2"
              rows={4}
              placeholder="Other phrases to avoid — one per line."
              value={avoidPhrasesText}
              onChange={(e) => setAvoidPhrasesText(e.target.value)}
            />
          </div>
        </div>

        {message && (
          <div
            className={`p-3 rounded-lg border text-sm ${
              message.type === "error"
                ? "bg-red-50 border-red-200 text-red-800"
                : message.type === "success"
                ? "bg-green-50 border-green-200 text-green-800"
                : "bg-blue-50 border-blue-200 text-blue-800"
            }`}
          >
            {message.text}
          </div>
        )}

        <button
          type="submit"
          disabled={loading}
          className="w-full bg-sabre-blue text-white py-3 rounded-lg font-medium hover:bg-sabre-blue/90 disabled:opacity-50"
        >
          {loading ? "Saving..." : "Save Settings"}
        </button>

        <button
          type="button"
          onClick={onDeleteProfile}
          disabled={loading}
          className="w-full bg-red-50 text-red-700 py-2 rounded-lg font-medium border border-red-200 hover:bg-red-100 disabled:opacity-50"
        >
          Delete Saved Voices
        </button>
      </form>
    </div>
  );
}